import type { ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { Card } from '@/components/Card';
import { Text } from '@/components/Text';
import { fontSizes, spacing } from '@/constants/theme';

export interface SectionCardProps {
  /** Section label ("Your progress", "FAQ", "Stuff your friends will ask"). */
  title: string;
  /** One-line context under the label. */
  subtitle?: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * The page-section container used below a screen's hero: a flat card with
 * an Inter Bold, Deep Green label one step larger than the rows inside it,
 * so it reads as a label for the group rather than a peer of the content.
 * One grammar for every section on the deal page and the Refer screens; a
 * page that mixes boxed and bare sections reads as unfinished.
 */
export function SectionCard({ title, subtitle, children, style, testID }: SectionCardProps) {
  return (
    <Card variant="flat" padding={0} style={[styles.card, style]} testID={testID}>
      <View style={styles.header}>
        <Text variant="bodyBold" color="deepGreen" style={styles.title}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="caption" color="textMuted">
            {subtitle}
          </Text>
        ) : null}
      </View>
      {children}
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.lg,
    gap: spacing.sm,
  },
  header: {
    gap: spacing.xs,
  },
  title: {
    fontSize: fontSizes.lg,
    lineHeight: 24,
  },
});
